/**
 * Hierarchy centerpiece views: the production-hierarchy backbone fronted as
 * two agent-facing, ancestry-enriched virtual views.
 *
 *   - Hierarchy        → one row per producing string with EVERY ancestor
 *                        (company / asset / field / reservoir / location / well)
 *                        resolved to a `{ space, externalId, name }` struct, plus
 *                        the string itself as a ref. Fronts `FlatHierarchy_Agent`.
 *   - SectorAllocation → one row per string's sector assignment: the sector ref,
 *                        the per-string `allocationFactor`, and the reservoir /
 *                        well ancestor names. Fronts `SectorHierarchy_Agent`.
 *
 * Both ride the ONE enriched factory (`createEnrichedView`): the source is read,
 * de-prefixed/typed via the shared `resolveColumnSet` SSOT, and the requested
 * ancestors are resolved through `enrichWithAncestors` (a single batched
 * `FlatHierarchy_Agent` read keyed on the producing-string ref, then a single
 * `Entity_Agent` read for ancestor names — both memoized via `ctx.session.fetch`).
 *
 * The driving ref is the producing-string relation, kept off the source `string`
 * column under the canonical name `wellString` (the SSOT name every per-string
 * view uses) so `enrich.stringRefColumn = 'wellString'` resolves it. `STRING_KEEP`
 * is NOT reusable here: its `wellString` column has suffix `'wellString'`, but the
 * hierarchy sources carry the producing string under the literal source name
 * `string` — so the keep maps source `string` → canonical `wellString` explicitly.
 * Identity (`externalId` / `space`) is the per-row hierarchy/sector instance, not
 * the string.
 *
 * SectorAllocation enriches only `reservoir` and `well`: `sector` is not an
 * `AncestorRelation` (it lives only on `SectorHierarchy_Agent`, with no name on
 * the entity registry), so it is kept as a raw ref alongside the scalar
 * `allocationFactor`. `reservoir` / `well` surface as named ancestor structs
 * (richer than the raw refs on the sector row), resolved — like every ancestor —
 * off the producing-string ref through `FlatHierarchy_Agent`.
 *
 * Built per session (not module-level) because the enriched views close over the
 * CDF client + hierarchy/entity views to custom-fetch and resolve ancestry.
 */

import { createEnrichedView, type EnrichSpec } from '../createEnrichedView.ts';
import type { VirtualView } from '../createReshapedView.ts';
import type { AncestorRelation } from '../enrichAncestors.ts';
import { IDENTITY, type Shape } from '../shapes/index.ts';
import { useCatalogEnrichContext } from './useCatalogEnrichContext.ts';
import { FlatHierarchyAgentView, SectorHierarchyAgentView } from '#schema/generated/ipso/views.ts';

/**
 * The producing-string ref carried by both hierarchy sources under the literal
 * source column `string`, renamed to the canonical `wellString` so the enrich
 * driver and any hierarchy-scoping filter use the SSOT name. (Distinct from
 * `STRING_RELATION`, whose source suffix is `wellString`.)
 */
const STRING_REF: Shape = [
  {
    canonical: 'wellString',
    suffix: 'string',
    description: 'Producing string (completion) this row describes.',
    kind: 'unitless',
  },
];

/** Identity + the producing-string ref: the passthrough both hierarchy views keep. */
const HIERARCHY_KEEP: Shape = [...IDENTITY, ...STRING_REF];

/** Every ancestor in the production hierarchy, resolved as a named struct. */
const ALL_ANCESTORS: readonly AncestorRelation[] = ['company', 'asset', 'field', 'reservoir', 'location', 'well'];

/** Hierarchy: enrich the full ancestry off the producing-string ref. */
const HIERARCHY_ENRICH: EnrichSpec = { stringRefColumn: 'wellString', relations: ALL_ANCESTORS };

/**
 * SectorAllocation: the sector ref + the per-string allocation factor kept off
 * `SectorHierarchy_Agent`; reservoir / well ancestor names come from the enrich
 * path (see module header). `allocationFactor` is a scalar whose type is derived
 * from the source schema by `resolveColumnSet`.
 */
const SECTOR_KEEP: Shape = [
  ...HIERARCHY_KEEP,
  {
    canonical: 'sector',
    suffix: 'sector',
    description: 'Subdivision of a reservoir used for flow allocation.',
    kind: 'unitless',
  },
  {
    canonical: 'allocationFactor',
    suffix: 'allocationFactor',
    description: 'Fractional flow allocation for this string within its sector.',
    kind: 'unitless',
  },
];

/** The ancestor names SectorAllocation surfaces (`sector` is not an ancestor relation). */
const SECTOR_ENRICH: EnrichSpec = { stringRefColumn: 'wellString', relations: ['reservoir', 'well'] };

export const useHierarchyViews = (): readonly VirtualView[] => {
  const { client, mintSeries } = useCatalogEnrichContext();
  return [
    createEnrichedView({
      name: 'Hierarchy',
      description:
        "One row per producing string with its full production-hierarchy ancestry: the string ref plus each ancestor (company, asset, field, reservoir, location, well) resolved to a { space, externalId, name } struct. Fronts the flattened hierarchy registry; filter on an ancestor's externalId to scope a subtree.",
      source: FlatHierarchyAgentView,
      keep: HIERARCHY_KEEP,
      enrich: HIERARCHY_ENRICH,
      client,
      mintSeries,
    }),
    createEnrichedView({
      name: 'SectorAllocation',
      description:
        "One row per string's sector assignment: the producing-string and sector refs, the fractional allocationFactor used in sector-based allocation, and the reservoir / well ancestor names resolved from the production hierarchy.",
      source: SectorHierarchyAgentView,
      keep: SECTOR_KEEP,
      enrich: SECTOR_ENRICH,
      client,
      mintSeries,
    }),
  ];
};
